import { useAuth } from "@/context/AuthContext";
import React, { useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { SteeringWheel, Buildings, Plus, X, UserPlus, PencilSimple, IdentificationCard, Eye } from "@phosphor-icons/react";
import { US_STATES, ENDORSEMENTS, PAY_TYPES, SALARY_FREQUENCIES } from "@/lib/constants";
import { toast } from "sonner";
import { DriverCopyBox } from "@/components/DriverCopyBox";

const emptyDriver = {
  name: "", phone: "", truck: "", trailer: "", vin: "",
  cdl_number: "", cdl_state: "", cdl_issue_date: "", cdl_expiration: "", endorsements: [], tracking_options: [],
  pay_type: "", per_mile_rate: "", pay_percentage: "", salary_amount: "", salary_frequency: "Weekly",
};

const TRACKING_OPTIONS = ["MacroPoint", "FourKites", "Trucker Tools", "Project44", "Descartes", "Motive / ELD Share", "Samsara ELD Share", "Tracking Link (Asterism)"];
const payLabel = (v) => PAY_TYPES.find((p) => p.value === v)?.label || "—";

function payDisplay(d) {
  switch (d.pay_type) {
    case "owner_operator": return "Owner-Operator (100%)";
    case "per_mile": return d.per_mile_rate ? `Per Mile · $${d.per_mile_rate}/mi` : "Per Mile";
    case "percentage": return d.pay_percentage ? `Percentage · ${d.pay_percentage}%` : "Percentage";
    case "salary": return d.salary_amount ? `Salary · $${d.salary_amount}/${d.salary_frequency || "Weekly"}` : "Salary";
    default: return "—";
  }
}

export default function DriverInfo() {
  const { user } = useAuth();
  const { data: allCarriers = [], isLoading } = useList("carriers", "/carriers");
  const carriers = user?.role === "admin" ? allCarriers : allCarriers.filter((c) => c.assigned_dispatcher === user?.id);
  const { data: drivers = [] } = useList("drivers", "/drivers");
  const invalidate = useInvalidate();
  const [carrierId, setCarrierId] = useState("");
  const [form, setForm] = useState(emptyDriver);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [assignId, setAssignId] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);

  const carrier = carriers.find((c) => c.id === carrierId);
  const assigned = drivers.filter((d) => d.carrier_id === carrierId);
  const unassigned = drivers.filter((d) => !d.carrier_id);
  const refresh = () => invalidate(["carriers", "drivers"]);

  const openNew = () => { setEditing(null); setForm(emptyDriver); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ ...emptyDriver, ...d, endorsements: d.endorsements || [], tracking_options: d.tracking_options || [] }); setOpen(true); };

  const toggleEnd = (code) => setForm((f) => ({ ...f, endorsements: f.endorsements.includes(code) ? f.endorsements.filter((x) => x !== code) : [...f.endorsements, code] }));

  const save = async () => {
    if (!carrierId && !editing) { toast.error("Select a carrier first"); return; }
    if (!form.name.trim()) { toast.error("Driver name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.pay_type === "owner_operator") { payload.per_mile_rate = ""; payload.pay_percentage = ""; payload.salary_amount = ""; }
      if (editing) await api.put(`/drivers/${editing.id}`, payload);
      else await api.post("/drivers", { ...payload, carrier_id: carrierId, status: "active" });
      toast.success(editing ? "Driver updated" : "Driver added");
      setOpen(false); setForm(emptyDriver); refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const assignExisting = async () => {
    if (!assignId) return;
    try { await api.put(`/drivers/${assignId}`, { carrier_id: carrierId }); setAssignId(""); refresh(); toast.success("Driver assigned"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const unassign = async (id) => {
    try { await api.put(`/drivers/${id}`, { carrier_id: "" }); refresh(); toast.success("Driver unassigned"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const inp = "bg-white border-slate-300";

  return (
    <div>
      <PageHeader title="Driver Info" subtitle="Select a trucking company to view and manage its assigned drivers"
        actions={carrierId ? <Button onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="add-driver-btn"><Plus size={16} weight="bold" className="mr-1.5" />Add Driver</Button> : null} />

      <div className="max-w-md mb-5">
        <Field label="Trucking Company">
          <Select value={carrierId} onValueChange={setCarrierId}>
            <SelectTrigger className={inp} data-testid="driverinfo-carrier-select"><SelectValue placeholder="Select a Carrier…" /></SelectTrigger>
            <SelectContent>{carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.carrier_name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      {isLoading ? <Panel><p className="p-6 text-slate-400">Loading…</p></Panel> : !carrierId ? (
        <Panel><EmptyState icon={Buildings} title="Choose a Carrier" hint={carriers.length ? "Pick a trucking company above to see its drivers." : "No carriers yet. Add them in Active Carriers."} /></Panel>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
          <Panel className="p-0">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
              <Buildings size={18} weight="duotone" className="text-blue-600" />
              <h3 className="font-heading font-bold text-slate-900">{carrier?.carrier_name}</h3>
              <span className="text-xs text-slate-500">· {assigned.length} drivers</span>
            </div>
            {assigned.length === 0 ? (
              <EmptyState icon={SteeringWheel} title="No Drivers Assigned" hint="Add a new driver or assign an existing one." />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-200 hover:bg-transparent">
                      <TableHead>Driver</TableHead>
                      <TableHead>Truck / Trailer</TableHead>
                      <TableHead>CDL</TableHead>
                      <TableHead>Endorsements</TableHead>
                      <TableHead>Tracking</TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assigned.map((d) => (
                      <TableRow key={d.id} className="border-slate-100" data-testid={`driverinfo-driver-${d.id}`}>
                        <TableCell><button onClick={() => setViewing(d)} className="font-medium text-slate-900 hover:text-blue-600 transition-colors text-left" data-testid={`view-driver-${d.id}`}>{d.name}</button><p className="font-mono text-xs text-slate-500">{d.phone || "—"}</p></TableCell>
                        <TableCell className="font-mono text-xs text-slate-600">{d.truck || "—"} / {d.trailer || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-600">{d.cdl_number ? <span className="block font-mono text-xs">{d.cdl_number}</span> : null}{d.cdl_state || "—"}{d.cdl_expiration ? <span className="block text-[11px] text-slate-400">Exp. {d.cdl_expiration}</span> : null}</TableCell>
                        <TableCell>{d.endorsements?.length ? <div className="flex flex-wrap gap-1">{d.endorsements.map((e) => <span key={e} className="inline-flex rounded bg-slate-100 text-slate-700 text-[10px] font-bold px-1.5 py-0.5">{e}</span>)}</div> : <span className="text-slate-400 text-sm">—</span>}</TableCell>
                        <TableCell className="text-xs text-slate-600">{(d.tracking_options || []).join(", ") || "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => setViewing(d)} className="p-1.5 text-slate-400 hover:text-blue-600" title="View & Copy" data-testid={`copy-driver-${d.id}`}><Eye size={15} /></button>
                            <button onClick={() => openEdit(d)} className="p-1.5 text-slate-400 hover:text-blue-600" title="Edit" data-testid={`edit-driver-${d.id}`}><PencilSimple size={15} /></button>
                            <button onClick={() => unassign(d.id)} className="p-1.5 text-slate-400 hover:text-red-500" title="Unassign" data-testid={`unassign-${d.id}`}><X size={15} /></button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Panel>

          <Panel className="p-5 h-fit">
            <h4 className="font-heading font-bold text-slate-900 mb-3 flex items-center gap-1.5"><UserPlus size={16} />Assign Existing</h4>
            <div className="flex gap-2">
              <Select value={assignId} onValueChange={setAssignId}>
                <SelectTrigger className={inp} data-testid="di-assign-select"><SelectValue placeholder="Unassigned Drivers…" /></SelectTrigger>
                <SelectContent>{unassigned.length === 0 ? <SelectItem value="none" disabled>No unassigned drivers</SelectItem> : unassigned.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={assignExisting} disabled={!assignId || assignId === "none"} className="bg-slate-800 text-white hover:bg-slate-900 shrink-0" data-testid="di-assign-btn">Assign</Button>
            </div>
          </Panel>
        </div>
      )}

      <DriverCopyBox driver={viewing} onClose={() => setViewing(null)} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Driver" : "Add Driver"}</DialogTitle><DialogDescription className="sr-only">Driver details</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Driver Name *"><Input data-testid="di-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></Field>
            <Field label="Phone Number"><Input data-testid="di-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} /></Field>
            <Field label="Truck #"><Input data-testid="di-truck" value={form.truck} onChange={(e) => setForm({ ...form, truck: e.target.value })} className={`${inp} font-mono`} /></Field>
            <Field label="Trailer #"><Input data-testid="di-trailer" value={form.trailer} onChange={(e) => setForm({ ...form, trailer: e.target.value })} className={`${inp} font-mono`} /></Field>
            <div className="sm:col-span-2"><Field label="VIN"><Input data-testid="di-vin" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className={`${inp} font-mono`} /></Field></div>

            <Field label="CDL State">
              <Select value={form.cdl_state || "none"} onValueChange={(v) => setForm({ ...form, cdl_state: v === "none" ? "" : v })}>
                <SelectTrigger className={inp} data-testid="di-cdl-state"><SelectValue placeholder="Select State" /></SelectTrigger>
                <SelectContent className="max-h-72"><SelectItem value="none">—</SelectItem>{US_STATES.map(([a, n]) => <SelectItem key={a} value={a}>{a} — {n}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="CDL No."><Input data-testid="di-cdl-number" value={form.cdl_number} onChange={(e) => setForm({ ...form, cdl_number: e.target.value })} className={`${inp} font-mono`} /></Field>
            <Field label="CDL Issue Date"><Input type="date" data-testid="di-cdl-issue" value={form.cdl_issue_date} onChange={(e) => setForm({ ...form, cdl_issue_date: e.target.value })} className={inp} /></Field>
            <Field label="CDL Expiration Date"><Input type="date" data-testid="di-cdl-exp" value={form.cdl_expiration} onChange={(e) => setForm({ ...form, cdl_expiration: e.target.value })} className={inp} /></Field>

            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><IdentificationCard size={16} className="text-blue-600" weight="duotone" />Endorsements & Credentials</label>
              <div className="flex flex-wrap gap-2">
                {ENDORSEMENTS.map((e) => {
                  const on = form.endorsements.includes(e.code);
                  return <button key={e.code} type="button" onClick={() => toggleEnd(e.code)} data-testid={`di-end-${e.code}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}>{e.label}</button>;
                })}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-slate-700 mb-2 block">Tracking Options Available</label>
              <div className="flex flex-wrap gap-2">
                {TRACKING_OPTIONS.map((t) => {
                  const on = form.tracking_options.includes(t);
                  return <button key={t} type="button" onClick={() => setForm((f) => ({ ...f, tracking_options: on ? f.tracking_options.filter((x) => x !== t) : [...f.tracking_options, t] }))} data-testid={`di-track-${t.replace(/\W+/g, "-").toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:border-emerald-400"}`}>{t}</button>;
                })}
              </div>
            </div>

            <div className="sm:col-span-2 border-t border-slate-200 pt-3">
              <Field label="Driver Pay Type">
                <Select value={form.pay_type || "none"} onValueChange={(v) => setForm({ ...form, pay_type: v === "none" ? "" : v })}>
                  <SelectTrigger className={inp} data-testid="di-pay-type"><SelectValue placeholder="Select Pay Type" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{PAY_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            {form.pay_type === "owner_operator" && (
              <div className="sm:col-span-2 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" data-testid="di-owneroperator-note">
                <b>Owner-Operator:</b> Receives <b>100%</b> of the load profit/revenue per the company's load accounting. No separate driver salary is calculated.
              </div>
            )}
            {form.pay_type === "per_mile" && (
              <Field label="Per Mile Rate ($/mile)"><Input type="number" step="0.01" data-testid="di-permile" value={form.per_mile_rate} onChange={(e) => setForm({ ...form, per_mile_rate: e.target.value })} className={`${inp} font-mono`} placeholder="0.65" /></Field>
            )}
            {form.pay_type === "percentage" && (
              <Field label="Percentage of Load Revenue">
                <div className="relative">
                  <Input type="number" data-testid="di-percentage" value={form.pay_percentage} onChange={(e) => setForm({ ...form, pay_percentage: e.target.value })} className={`${inp} font-mono pr-8`} placeholder="20" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>
                </div>
              </Field>
            )}
            {form.pay_type === "salary" && (
              <>
                <Field label="Salary Amount ($)"><Input type="number" data-testid="di-salary" value={form.salary_amount} onChange={(e) => setForm({ ...form, salary_amount: e.target.value })} className={`${inp} font-mono`} /></Field>
                <Field label="Frequency">
                  <Select value={form.salary_frequency} onValueChange={(v) => setForm({ ...form, salary_frequency: v })}>
                    <SelectTrigger className={inp} data-testid="di-salary-freq"><SelectValue /></SelectTrigger>
                    <SelectContent>{SALARY_FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="di-save-btn">{saving ? "Saving…" : editing ? "Save" : "Add Driver"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
